(function() {
    'use strict';

    // ========= CONFIG =========
    const CONFIG = {
        apiEndpoint: 'https://api.inflowpay.xyz',
        shopifyToken: 'wHTFxDmuMh2S59gUt7sGyd8F16LqxUCt',
        successUrl: window.location.origin + '/pages/payment-success',
        debug: false // Toggle for console logs
    };

    // ========= CONSTANTS =========
    const COLORS = {
        error: '#fc8181',
        default: '#e2e8f0'
    };

    const REQUIRED_FIELDS = ['firstName', 'lastName', 'address1', 'city', 'country', 'zip'];

    // ========= Utils =========
    const $id = (id) => document.getElementById(id);

    function log(...args) {
        if (CONFIG.debug) console.log(...args);
    }

    function setBtnLoading(btn, loading) {
        if (!btn) return;

        if (loading) {
            btn.dataset.originalHtml = btn.innerHTML;
            btn.disabled = true;
            const firstSpan = btn.querySelector('span');
            if (firstSpan) {
                firstSpan.innerHTML = 'Processing...';
            }
        } else {
            btn.disabled = false;
            if (btn.dataset.originalHtml) {
                btn.innerHTML = btn.dataset.originalHtml;
                delete btn.dataset.originalHtml;
            }
        }
    }

    // Unified field styling function
    function setFieldStyle(fieldName, isError) {
        const color = isError ? COLORS.error : COLORS.default;

        if (fieldName === 'country') {
            const countryBtn = $id('inflow-shipping-country-btn');
            if (countryBtn) {
                countryBtn.style.borderColor = color;
            }
        } else {
            const inputField = $id('inflow-shipping-' + fieldName);
            if (inputField) {
                inputField.style.borderColor = color;
            }
        }
    }

    // Show error for specific field
    function showFieldError(fieldName, msg) {
        const errorDiv = $id('error-' + fieldName);
        if (errorDiv) {
            errorDiv.textContent = msg;
            errorDiv.style.display = 'block';
        }
        setFieldStyle(fieldName, true);
    }

    // Clear error for specific field
    function clearFieldError(fieldName) {
        const errorDiv = $id('error-' + fieldName);
        if (errorDiv) {
            errorDiv.style.display = 'none';
            errorDiv.textContent = '';
        }
        setFieldStyle(fieldName, false);
    }

    // Clear all field errors
    function clearAllFieldErrors() {
        REQUIRED_FIELDS.forEach(clearFieldError);
    }

    // General error message (for API errors)
    function showError(msg) {
        const box = $id('inflow-error-message');
        if (!box) return;
        box.textContent = msg || 'An error occurred. Please try again.';
        box.style.display = 'block';
    }

    function clearError() {
        const box = $id('inflow-error-message');
        if (!box) return;
        box.style.display = 'none';
        box.textContent = '';
    }

    // ========= Shipping Address =========
    function readShippingAddress() {
        return {
            firstName: $id('inflow-shipping-firstName')?.value?.trim() || '',
            lastName: $id('inflow-shipping-lastName')?.value?.trim() || '',
            address1: $id('inflow-shipping-address1')?.value?.trim() || '',
            address2: $id('inflow-shipping-address2')?.value?.trim() || '',
            city: $id('inflow-shipping-city')?.value?.trim() || '',
            province: $id('inflow-shipping-province')?.value?.trim() || '',
            country: $id('inflow-shipping-country')?.value?.trim() || '',
            zip: $id('inflow-shipping-zip')?.value?.trim() || '',
            phone: $id('inflow-shipping-phone')?.value?.trim() || '',
        };
    }

    function validateShippingAddress(addr) {
        clearAllFieldErrors();

        const validationRules = {
            firstName: 'First name is required',
            lastName: 'Last name is required',
            address1: 'Address is required',
            city: 'City is required',
            country: 'Please select a country',
            zip: 'Zip code is required'
        };

        let isValid = true;

        for (const [field, message] of Object.entries(validationRules)) {
            if (!addr[field]) {
                showFieldError(field, message);
                isValid = false;
            }
        }

        return { ok: isValid };
    }

    // ========= API Calls =========
    async function getCart() {
        const res = await fetch('/cart.js', {
            credentials: 'same-origin'
        });
        if (!res.ok) throw new Error('Failed to fetch cart.');
        return res.json();
    }

    async function generatePayment(cart, shippingAddressJSON) {
        const payload = {
            items: cart.items.map((it) => ({
                title: it.title,
                price: Math.round(it.price),
                quantity: it.quantity,
                variantId: it.variant_id
            })),
            customerEmail: cart.attributes?.email || undefined,
            shippingAddress: shippingAddressJSON,
            successUrl: CONFIG.successUrl,
        };

        log('📦 Payload → Inflow:', payload);

        const res = await fetch(`${CONFIG.apiEndpoint}/shopify/generate-payment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-shopify-token': CONFIG.shopifyToken,
            },
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            let detail = '';
            try {
                const errorData = await res.json();
                detail = errorData.message || '';
            } catch (parseError) {
                // Response is not JSON, use default error message
                log('Failed to parse error response:', parseError);
            }
            throw new Error(detail || `Inflow API error (${res.status})`);
        }
        return res.json();
    }

    // ========= Main Checkout Flow =========
    async function startInflowCheckout(e) {
        if (e) e.preventDefault();
        clearError();
        clearAllFieldErrors();

        const btn = $id('inflow-checkout-btn');
        setBtnLoading(btn, true);

        try {
            const cart = await getCart();
            if (!cart.items?.length) {
                showError('Your cart is empty.');
                setBtnLoading(btn, false);
                return;
            }

            const addr = readShippingAddress();
            const validation = validateShippingAddress(addr);
            if (!validation.ok) {
                setBtnLoading(btn, false);
                return;
            }

            const result = await generatePayment(cart, JSON.stringify(addr));
            log('✅ Payment created:', result);

            // Pre-fill name parameters on Inflow checkout
            const url = new URL(result.checkoutUrl);
            if (addr.firstName) url.searchParams.set('firstName', addr.firstName);
            if (addr.lastName) url.searchParams.set('lastName', addr.lastName);

            window.location.href = url.toString();
        } catch (err) {
            console.error('❌ Inflow checkout error:', err);
            setBtnLoading(btn, false);
        }
    }

    // ========= Event Listeners =========

    // Make available globally if needed
    window.startInflowCheckout = startInflowCheckout;

    // Click delegation for checkout button
    document.addEventListener('click', function(evt) {
        if (evt.target.closest('#inflow-checkout-btn')) {
            startInflowCheckout(evt);
        }
    });

    // Clear field errors on input
    REQUIRED_FIELDS.forEach(function(fieldName) {
        const field = $id('inflow-shipping-' + fieldName);
        if (field) {
            field.addEventListener('input', function() {
                clearFieldError(fieldName);
            });
        }
    });

    // Toggle country dropdown
    const countryBtn = $id('inflow-shipping-country-btn');
    if (countryBtn) {
        countryBtn.addEventListener('click', function() {
            const countryList = $id('inflow-country-list');
            if (countryList) {
                countryList.toggleAttribute('hidden');
            }
        });
    }

    // Handle country selection
    document.addEventListener('click', function(e) {
        const countryOption = e.target.closest('.country-option');
        if (countryOption) {
            e.preventDefault();

            const countryCode = countryOption.dataset.countryCode;
            const countryName = countryOption.dataset.countryName;

            const countryInput = $id('inflow-shipping-country');
            const countrySelected = $id('inflow-country-selected');
            const countryList = $id('inflow-country-list');

            if (countryInput) countryInput.value = countryCode;
            if (countrySelected) {
                countrySelected.textContent = countryName;
                countrySelected.className = 'selected';
            }
            if (countryList) countryList.setAttribute('hidden', 'true');

            clearFieldError('country');
        }
    });

    // Close country dropdown when clicking outside
    document.addEventListener('click', function(e) {
        const countryBtn = $id('inflow-shipping-country-btn');
        const countryList = $id('inflow-country-list');

        if (countryBtn && countryList &&
            !countryBtn.contains(e.target) &&
            !countryList.contains(e.target)) {
            countryList.setAttribute('hidden', 'true');
        }
    });

    log('🎉 Inflow script ready');
})();